import { useState, useEffect, useMemo, useRef } from 'react'
import { useFormDraft } from '../hooks/useFormDraft'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Shield, Loader2, Check, Plus, X, ArrowRight, ArrowLeft, User, MessageSquare,
  ShieldCheck, Pencil, AlertTriangle, Settings, Camera, Trash2, Copy,
  FileText, RefreshCcw,
} from 'lucide-react'
import { notify } from '../components/Toast'
import AvatarCropper from '../components/AvatarCropper'
import KnowledgeSourcesPanel from '../components/KnowledgeSourcesPanel'
import {
  listMyPersonas, createPersona, updatePersona, consentPersona, checkHandle, uploadKnowledgeSource,
  changeHandle, getAvailableModels, getOptionalRules, listKnowledgeSources,
  type ModelOption as AvailableModel,
} from '../api/client'

type Step = 'identity' | 'scope' | 'behavior' | 'review'

const STEPS: { key: Step; label: string; icon: React.ElementType }[] = [
  { key: 'identity', label: 'Identity',      icon: User },
  { key: 'scope',    label: 'Scope & Rules', icon: MessageSquare },
  { key: 'behavior', label: 'Behavior',      icon: Settings },
  { key: 'review',   label: 'Review',        icon: ShieldCheck },
]

const TONES = ['professional', 'friendly', 'formal', 'casual', 'empathetic'] as const

export default function PersonaSetup() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === '1'
  const editId = searchParams.get('id')

  // Always fetch the full list — it drives both explicit edit-by-id and the
  // legacy "my single persona" default. Cheap (small array) and keeps a single
  // source of truth for dashboard ↔ setup flows.
  const { data: personas = [] } = useQuery({ queryKey: ['my-personas'], queryFn: listMyPersonas, retry: false })
  const {
    data: models,
    isError: modelsError,
    isLoading: modelsLoading,
    refetch: refetchModels,
  } = useQuery({ queryKey: ['available-models'], queryFn: getAvailableModels, retry: false })
  const { data: optionalRules } = useQuery({ queryKey: ['optional-rules'], queryFn: getOptionalRules, retry: false })

  const existing = useMemo(() => {
    if (isNew) return null
    if (editId) return personas.find((p) => p.id === editId) || null
    return personas[0] || null
  }, [isNew, editId, personas])

  const isEditing = Boolean(existing?.id)

  // Shared cache with KnowledgeSourcesPanel — used on the Review step to
  // surface uploaded files alongside scope rules.
  const { data: knowledgeData } = useQuery({
    queryKey: ['knowledge-sources', existing?.id],
    queryFn: () => listKnowledgeSources(existing?.id as string),
    enabled: Boolean(existing?.id),
  })
  const knowledgeSources = knowledgeData?.sources ?? []

  const [step, setStep] = useState<Step>('identity')
  const [handle, setHandle] = useState('')
  const [name, setName] = useState('')
  const [type, setType] = useState('individual')
  const [personaPhoto, setPersonaPhoto] = useState('')
  const [cropSrc, setCropSrc] = useState<string | null>(null)
  const [publicBio, setPublicBio] = useState('')
  const [description, setDescription] = useState('')
  const [knowledgeBase, setKnowledgeBase] = useState('')
  const [tone, setTone] = useState('')
  const [boundaries, setBoundaries] = useState('')
  const [strictness, setStrictness] = useState('STRICT')
  const [aiInstructions, setAiInstructions] = useState('')
  const [refusalMessage, setRefusalMessage] = useState('')
  const [allowGeneralTasks, setAllowGeneralTasks] = useState(false)
  const [responseLength, setResponseLength] = useState('MEDIUM')
  const [preferredModel, setPreferredModel] = useState('')
  const [upgradeModel, setUpgradeModel] = useState<AvailableModel | null>(null)
  const [disabledRules, setDisabledRules] = useState<string[]>([])
  const [canHelpWith, setCanHelpWith] = useState<string[]>([])
  const [cannotHelpWith, setCannotHelpWith] = useState<string[]>([])
  const [ownerDisplayName, setOwnerDisplayName] = useState('')
  const [newCanHelp, setNewCanHelp] = useState('')
  const [newCannotHelp, setNewCannotHelp] = useState('')
  const [saving, setSaving] = useState(false)
  const [handleAvailable, setHandleAvailable] = useState<boolean | null>(null)
  const [handleValid, setHandleValid] = useState<boolean | null>(null)
  const [handleError, setHandleError] = useState<string | null>(null)
  const [changingHandle, setChangingHandle] = useState(false)
  const [newHandle, setNewHandle] = useState('')
  const [newHandleAvailable, setNewHandleAvailable] = useState<boolean | null>(null)
  const [newHandleError, setNewHandleError] = useState<string | null>(null)
  // Captures the id after a successful create in the "new persona" flow, so the
  // subsequent consent step knows which persona to activate before the list refetch lands.
  const [justCreatedId, setJustCreatedId] = useState<string | null>(null)
  // Files queued before the persona exists on the server. Flushed after
  // createPersona returns an id — see handleSave below. Not part of the
  // localStorage draft (File objects aren't serialisable + usually large).
  const [pendingKnowledgeFiles, setPendingKnowledgeFiles] = useState<File[]>([])

  useEffect(() => {
    if (existing?.id) {
      setHandle(existing.handle || '')
      setName(existing.name || '')
      setType(existing.type || 'individual')
      setPersonaPhoto(existing.personaPhoto || '')
      setPublicBio(existing.publicBio || '')
      setDescription(existing.description || '')
      setKnowledgeBase(existing.knowledgeBase || '')
      setTone(existing.tone || '')
      setBoundaries(existing.boundaries || '')
      setStrictness(existing.strictness || 'STRICT')
      setAiInstructions(existing.aiInstructions || '')
      setRefusalMessage(existing.refusalMessage || '')
      setAllowGeneralTasks(Boolean(existing.allowGeneralTasks))
      setResponseLength(existing.responseLength || 'MEDIUM')
      setPreferredModel(existing.preferredModel || '')
      setDisabledRules(existing.disabledRules || [])
      setCanHelpWith(existing.canHelpWith || [])
      setCannotHelpWith(existing.cannotHelpWith || [])
      setOwnerDisplayName(existing.ownerDisplayName || '')
    }
  }, [existing])

  // ── Draft persistence ────────────────────────────────────────────────
  // Save all user-typed fields to localStorage so a reload / tab-close /
  // accidental nav-away doesn't wipe the form. Keyed per-persona so drafts
  // of different edits don't cross-contaminate. Cleared on successful save.
  const draftValues = useMemo(() => ({
    handle, name, type, personaPhoto, publicBio, description, knowledgeBase,
    tone, boundaries, strictness, aiInstructions, refusalMessage,
    allowGeneralTasks, responseLength, preferredModel, disabledRules,
    canHelpWith, cannotHelpWith, ownerDisplayName,
  }), [handle, name, type, personaPhoto, publicBio, description, knowledgeBase,
       tone, boundaries, strictness, aiInstructions, refusalMessage,
       allowGeneralTasks, responseLength, preferredModel, disabledRules,
       canHelpWith, cannotHelpWith, ownerDisplayName])

  const { clearDraft } = useFormDraft({
    key: `persona-draft:${existing?.id ?? 'new'}`,
    values: draftValues,
    // Defer until the server-load hydration above has run so a freshly-opened
    // edit page doesn't get overwritten by a stale draft.
    disabled: Boolean(personas.length && !existing && searchParams.get('id')),
    onRestore: (d) => {
      if (d.handle !== undefined) setHandle(d.handle || '')
      if (d.name !== undefined) setName(d.name || '')
      if (d.type !== undefined) setType(d.type || 'individual')
      if (d.personaPhoto !== undefined) setPersonaPhoto(d.personaPhoto || '')
      if (d.publicBio !== undefined) setPublicBio(d.publicBio || '')
      if (d.description !== undefined) setDescription(d.description || '')
      if (d.knowledgeBase !== undefined) setKnowledgeBase(d.knowledgeBase || '')
      if (d.tone !== undefined) setTone(d.tone || '')
      if (d.boundaries !== undefined) setBoundaries(d.boundaries || '')
      if (d.strictness !== undefined) setStrictness(d.strictness || 'STRICT')
      if (d.aiInstructions !== undefined) setAiInstructions(d.aiInstructions || '')
      if (d.refusalMessage !== undefined) setRefusalMessage(d.refusalMessage || '')
      if (d.allowGeneralTasks !== undefined) setAllowGeneralTasks(Boolean(d.allowGeneralTasks))
      if (d.responseLength !== undefined) setResponseLength(d.responseLength || 'MEDIUM')
      if (d.preferredModel !== undefined) setPreferredModel(d.preferredModel || '')
      if (d.disabledRules !== undefined) setDisabledRules(d.disabledRules || [])
      if (d.canHelpWith !== undefined) setCanHelpWith(d.canHelpWith || [])
      if (d.cannotHelpWith !== undefined) setCannotHelpWith(d.cannotHelpWith || [])
      if (d.ownerDisplayName !== undefined) setOwnerDisplayName(d.ownerDisplayName || '')
      // Stable id so React StrictMode's dev-only double-mount (which re-runs
      // useFormDraft's restore effect twice) shows a single toast, not two —
      // react-hot-toast replaces any existing toast with the same id.
      notify.info('Restored your in-progress persona from a previous session.',
            { duration: 4000, id: 'persona-draft-restored' })
    },
  })

  // Handle validation for new personas (debounced availability check)
  useEffect(() => {
    setHandleAvailable(null); setHandleValid(null); setHandleError(null)
    if (!handle || handle.length < 3) return
    if (existing?.handle === handle) { setHandleAvailable(true); setHandleValid(true); return }
    const t = setTimeout(async () => {
      try {
        const r = await checkHandle(handle)
        setHandleAvailable(r.available)
        setHandleValid(r.valid)
        setHandleError(r.valid ? null : (r.reason || 'Invalid handle'))
      } catch (_) { setHandleAvailable(null); setHandleValid(null); setHandleError(null) }
    }, 500)
    return () => clearTimeout(t)
  }, [handle, existing])

  // Handle validation for change flow
  useEffect(() => {
    setNewHandleAvailable(null)
    setNewHandleError(null)
    if (!newHandle || newHandle.length < 3) return
    if (existing?.handle === newHandle) { setNewHandleError('This is already your current handle'); return }
    const t = setTimeout(async () => {
      try {
        const r = await checkHandle(newHandle)
        setNewHandleAvailable(r.available && r.valid)
        setNewHandleError(!r.valid ? (r.reason || 'Invalid') : !r.available ? 'This handle is not available' : null)
      } catch (_) { setNewHandleAvailable(null); setNewHandleError(null) }
    }, 500)
    return () => clearTimeout(t)
  }, [newHandle, existing])

  const confirmHandleChange = async () => {
    if (!existing?.id || !newHandle || !newHandleAvailable) return
    setSaving(true)
    try {
      await changeHandle(existing.id, newHandle)
      await queryClient.refetchQueries({ queryKey: ['my-personas'] })
      setHandle(newHandle)
      setChangingHandle(false)
      setNewHandle('')
      notify.success('Handle changed. All invite links have been revoked. Re-consent required.')
    } catch (err) { notify.error((err as { message?: string })?.message || 'Unable to change handle. Please try again.') }
    finally { setSaving(false) }
  }

  const handleSave = async () => {
    if (!handle || !name) { notify.error('Please provide a handle and persona name.'); return }
    setSaving(true)
    try {
      const data = {
        handle: handle.toLowerCase(), name, type, personaPhoto: personaPhoto || undefined,
        publicBio, description, knowledgeBase, tone, boundaries, canHelpWith, cannotHelpWith,
        ownerDisplayName, strictness, aiInstructions, refusalMessage, allowGeneralTasks,
        responseLength, preferredModel, disabledRules,
      }
      let targetId: string | null = existing?.id ?? null
      if (existing?.id) {
        await updatePersona(existing.id, data)
        notify.success('Changes saved.')
      } else {
        const created = await createPersona(data)
        if (created?.id) {
          setJustCreatedId(created.id)
          targetId = created.id
        }
        notify.success('Persona created successfully.')
      }

      // Flush any files queued before the persona existed on the server. We
      // upload sequentially so the entitlement counter + total-bytes caps
      // apply deterministically, and we surface per-file errors without
      // aborting the rest of the batch.
      if (targetId && pendingKnowledgeFiles.length > 0) {
        for (const file of pendingKnowledgeFiles) {
          try {
            await uploadKnowledgeSource(targetId, file)
          } catch (err) {
            notify.error(`Couldn't upload ${file.name}: ${(err as { message?: string })?.message || 'unknown error'}`)
          }
        }
        setPendingKnowledgeFiles([])
        queryClient.invalidateQueries({ queryKey: ['knowledge-sources', targetId] })
      }

      queryClient.invalidateQueries({ queryKey: ['my-personas'] })
      clearDraft()   // discard the autosaved in-progress draft now that it's on the server
      return true
    } catch (err) { notify.error((err as { message?: string })?.message || 'Unable to save. Please try again.'); return false }
    finally { setSaving(false) }
  }

  const handleConsent = async () => {
    const personaId = existing?.id || justCreatedId
    if (!personaId) { notify.error('Save your persona first.'); return }
    setSaving(true)
    try {
      await consentPersona(personaId)
      notify.success('Your persona is now active!')
      queryClient.invalidateQueries({ queryKey: ['my-personas'] })
      navigate('/dashboard')
    } catch (err) { notify.error((err as { message?: string })?.message || 'Unable to activate. Please try again.') }
    finally { setSaving(false) }
  }

  const addTag = (list: string[], setter: (v: string[]) => void, value: string, clearFn: (v: string) => void) => {
    const v = value.trim()
    if (v && !list.includes(v)) { setter([...list, v]); clearFn('') }
  }

  const toggleTone = (t: string) => {
    const parts = tone.split(',').map((s) => s.trim()).filter(Boolean)
    const selected = parts.map((s) => s.toLowerCase()).includes(t)
    if (selected) setTone(parts.filter((s) => s.toLowerCase() !== t).join(', '))
    else setTone([...parts, t].join(', '))
  }

  const canProceedIdentity = handle.length >= 3 && handleAvailable !== false && handleValid !== false && !changingHandle && name.length > 0
  const canProceedScope = canHelpWith.length > 0
  // Gate Save & review when the stored preferred model is no longer in the
  // caller's allowed set (tier was downgraded between load and save).
  const preferredModelLocked = useMemo(() => {
    if (!preferredModel) return false
    const m = models?.find((x) => x.id === preferredModel)
    return m ? Boolean(m.locked) : false
  }, [preferredModel, models])
  // Also guard while the catalog is still loading so a user with a stored
  // preferredModel can't slip through before the lock check lands.
  const canProceedBehavior = !preferredModelLocked
    && !(preferredModel && modelsLoading)

  const goNext = async () => {
    if (step === 'identity') {
      if (!canProceedIdentity) { notify.error('Please complete all required fields.'); return }
      setStep('scope')
    } else if (step === 'scope') {
      if (!canProceedScope) { notify.error('Add at least one topic your persona can help with.'); return }
      setStep('behavior')
    } else if (step === 'behavior') {
      const ok = await handleSave()
      if (ok) {
        await queryClient.refetchQueries({ queryKey: ['my-personas'] })
        setStep('review')
      }
    }
  }

  const goBack = () => {
    if (step === 'scope') setStep('identity')
    else if (step === 'behavior') setStep('scope')
    else if (step === 'review') setStep('behavior')
  }

  const currentIdx = STEPS.findIndex((s) => s.key === step)

  const publicUrl = `idfy.studio/p/${handle || '...'}`
  const status: { label: string; tone: 'success' | 'warn' | 'neutral' } = existing?.isActive
    ? { label: 'Active', tone: 'success' }
    : isEditing
      ? { label: 'Draft', tone: 'warn' }
      : { label: 'New', tone: 'neutral' }

  return (
    <div className="max-w-4xl mx-auto py-6 md:py-10 px-4 space-y-5 md:space-y-6 pb-24">
      {/* Header */}
      <header className="space-y-3">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[rgb(var(--text-muted))]">
          <Shield size={12} strokeWidth={2.25} />
          <span>Persona workspace</span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-[26px] leading-tight font-semibold tracking-tight text-gray-900 dark:text-gray-100">
              {isEditing ? 'Edit Persona' : 'Create Persona'}
            </h1>
            <p className="mt-1.5 text-sm text-[rgb(var(--text-muted))] max-w-2xl">
              {isEditing
                ? 'Update your persona settings, scope, and behavior. Scope changes require re-consent.'
                : 'Set up an AI persona that represents you or your business — within the boundaries you define.'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusChip tone={status.tone}>{status.label}</StatusChip>
            <TypeChip>{type === 'business' ? 'Business' : 'Individual'}</TypeChip>
          </div>
        </div>
      </header>

      {/* Stepper */}
      <section aria-label="Setup progress">
        <nav
          aria-label={`Wizard step ${currentIdx + 1} of ${STEPS.length}: ${STEPS[currentIdx].label}`}
          className="wizard-stepper"
        >
          {STEPS.map((s, i) => {
            const state: 'active' | 'done' | 'upcoming' =
              s.key === step ? 'active' : i < currentIdx ? 'done' : 'upcoming'
            const Icon = s.icon
            return (
              <div key={s.key} className="wizard-step">
                <button
                  type="button"
                  onClick={() => { if (state === 'done') setStep(s.key) }}
                  disabled={state === 'upcoming'}
                  aria-current={state === 'active' ? 'step' : undefined}
                  data-state={state}
                  className="wizard-step-pill"
                >
                  <span className="wizard-step-pill__index">
                    {state === 'done' ? <Check size={11} strokeWidth={3} /> : i + 1}
                  </span>
                  <Icon size={14} strokeWidth={1.8} className="shrink-0" />
                  <span className="hidden sm:inline truncate">{s.label}</span>
                </button>
                {i < STEPS.length - 1 && (
                  <span className="wizard-step-connector" data-done={i < currentIdx ? 'true' : 'false'} aria-hidden="true" />
                )}
              </div>
            )
          })}
        </nav>
      </section>

      {/* Step 1: Identity — three cards by audience: who / what visitors see / AI-only. */}
      {step === 'identity' && (
        <div className="space-y-6">

          {/* Card 1 — Identity: who this persona is */}
          <div className="wizard-card">
            <div>
              <div className="wizard-card__eyebrow">Identity</div>
              <p className="wizard-card__subtitle mt-1">
                Who this persona represents. Required to create or update.
              </p>
            </div>

            <div className="flex flex-col md:flex-row gap-6">
              <PersonaPhotoUploader
                photoUrl={personaPhoto}
                onChoose={(src) => setCropSrc(src)}
                onClear={() => setPersonaPhoto('')}
              />

              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4">
                  <div>
                    <label htmlFor="persona-name" className="field-label">
                      Persona name <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="persona-name" className="field-input" value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Dr. Smith's Office" required
                    />
                  </div>
                  <div>
                    <span className="field-label">Type</span>
                    <div
                      className="segmented mt-0.5" role="group" aria-label="Persona type"
                    >
                      {(['individual', 'business'] as const).map((v) => (
                        <button
                          key={v} type="button" onClick={() => setType(v)}
                          className="segmented__item"
                          aria-pressed={type === v}
                        >
                          {v === 'individual' ? 'Individual' : 'Business'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <label htmlFor="owner-display" className="field-label">
                    Owner display name <span className="text-[rgb(var(--text-muted))] font-normal normal-case">(optional)</span>
                  </label>
                  <input
                    id="owner-display" className="field-input" value={ownerDisplayName}
                    onChange={(e) => setOwnerDisplayName(e.target.value)}
                    placeholder="Name shown on the public page"
                  />
                  <p className="field-hint">Shown on the public chat page as the persona's owner.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2 — Public Presence: what visitors see at the URL */}
          <div className="wizard-card">
            <div>
              <div className="wizard-card__eyebrow">Public presence</div>
              <p className="wizard-card__subtitle mt-1">
                The URL visitors use and what they see and hear on the chat page.
              </p>
            </div>

            <HandleField
              isEditing={isEditing}
              changingHandle={changingHandle}
              handle={handle}
              onHandleChange={setHandle}
              handleValid={handleValid}
              handleAvailable={handleAvailable}
              handleError={handleError}
              newHandle={newHandle}
              onNewHandleChange={setNewHandle}
              newHandleAvailable={newHandleAvailable}
              newHandleError={newHandleError}
              onStartChange={() => { setChangingHandle(true); setNewHandle('') }}
              onCancelChange={() => setChangingHandle(false)}
              onConfirmChange={confirmHandleChange}
              saving={saving}
              publicUrl={publicUrl}
            />

            <div>
              <label htmlFor="public-bio" className="field-label">
                Public bio <span className="text-[rgb(var(--text-muted))] font-normal normal-case">(optional)</span>
              </label>
              <p className="field-hint -mt-1 mb-2">A short intro shown at the top of the chat page.</p>
              <textarea
                id="public-bio" className="field-input min-h-[80px] py-2.5 leading-relaxed"
                value={publicBio} onChange={(e) => setPublicBio(e.target.value)}
                placeholder="A brief description of who this persona represents..."
                maxLength={300}
              />
              <div className="flex items-center justify-between mt-1">
                {publicBio.length === 0
                  ? <span className="text-[11px] text-[rgb(var(--text-muted))]">Keep it short — one or two sentences works best.</span>
                  : <span />}
                <span className="text-[11px] text-[rgb(var(--text-muted))] tabular-nums">{publicBio.length}/300</span>
              </div>
            </div>

            <div>
              <span className="field-label">
                Tone <span className="text-[rgb(var(--text-muted))] font-normal normal-case">(optional)</span>
              </span>
              <p className="field-hint -mt-1 mb-2">Pick one or more styles. Visitors see the combined tone applied to every reply.</p>
              <div className="flex flex-wrap gap-2">
                {TONES.map((t) => {
                  const selected = tone.toLowerCase().split(',').map((s) => s.trim()).includes(t)
                  return (
                    <button
                      key={t} type="button" onClick={() => toggleTone(t)}
                      aria-pressed={selected}
                      className={`px-3 h-8 rounded-full text-[13px] font-medium border transition-colors ${
                        selected
                          ? 'border-brand-500 bg-brand-500/15 text-brand-600 dark:text-brand-300'
                          : 'border-[rgb(var(--border-subtle))] text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--text-primary))] hover:bg-[rgb(var(--surface-3)/0.5)]'
                      }`}
                    >
                      {t}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Card 3 — AI Notes: internal context the AI can use, never shown */}
          <div className="wizard-card">
            <div>
              <div className="wizard-card__eyebrow">AI notes</div>
              <p className="wizard-card__subtitle mt-1">
                Background the AI can use when it answers. Never shown to visitors.
              </p>
            </div>

            <div>
              <label htmlFor="description" className="field-label">
                Internal notes <span className="text-[rgb(var(--text-muted))] font-normal normal-case">(optional)</span>
              </label>
              <textarea
                id="description"
                className="field-input min-h-[96px] py-2.5 leading-relaxed"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Company history, services, team info, key differentiators…"
                maxLength={2000}
              />
              <div className="flex items-center justify-end mt-1">
                <span className="text-[11px] text-[rgb(var(--text-muted))] tabular-nums">{description.length}/2,000</span>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* Step 2: Scope & Knowledge — two cards by purpose:
          Scope = rules of engagement (what to do / not do / must-follow rules).
          Knowledge = reference material the AI can read (files + freeform). */}
      {step === 'scope' && (
        <div className="space-y-6">

          {/* Card 1 — Scope: rules of engagement */}
          <div className="wizard-card">
            <div>
              <div className="wizard-card__eyebrow">Scope</div>
              <p className="wizard-card__subtitle mt-1">
                Define what your persona should help visitors with, what it must refuse, and the rules it must follow.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <TagInput
                id="can-help"
                label="Can help with"
                required
                helper="Topics your persona is authorized to discuss."
                value={canHelpWith}
                onChange={setCanHelpWith}
                current={newCanHelp}
                onCurrentChange={setNewCanHelp}
                placeholder="e.g. sales, support, scheduling"
                examples={['sales', 'support', 'scheduling']}
                tone="positive"
                onAdd={() => addTag(canHelpWith, setCanHelpWith, newCanHelp, setNewCanHelp)}
              />

              <TagInput
                id="cannot-help"
                label="Cannot help with"
                helper="Topics your persona must refuse. Keeps responses within scope."
                value={cannotHelpWith}
                onChange={setCannotHelpWith}
                current={newCannotHelp}
                onCurrentChange={setNewCannotHelp}
                placeholder="e.g. medical advice, legal"
                examples={['medical advice', 'legal advice', 'financial advice']}
                tone="danger"
                onAdd={() => addTag(cannotHelpWith, setCannotHelpWith, newCannotHelp, setNewCannotHelp)}
              />
            </div>

            <div>
              <label htmlFor="boundaries" className="field-label">
                Boundary rules <span className="text-[rgb(var(--text-muted))] font-normal normal-case">(optional)</span>
              </label>
              <p className="field-hint -mt-1 mb-2">
                Must-follow instructions for the AI — what to always do, what never to do.
              </p>
              <textarea
                id="boundaries" className="field-input min-h-[96px] py-2.5 leading-relaxed"
                value={boundaries} onChange={(e) => setBoundaries(e.target.value)}
                placeholder={'e.g. Never share pricing without approval. Always recommend scheduling a call for complex requests.'}
              />
              <p className="field-hint mt-2">
                Good examples: <span className="text-[rgb(var(--text-secondary))]">
                  "Recommend a human handoff for refunds"
                </span>, <span className="text-[rgb(var(--text-secondary))]">
                  "Never share internal employee names"
                </span>.
              </p>
            </div>
          </div>

          {/* Card 2 — Knowledge: reference material */}
          <div className="wizard-card">
            <KnowledgeSourcesPanel
              personaId={existing?.id ?? null}
              chrome="bare"
              pendingFiles={pendingKnowledgeFiles}
              onPendingFilesChange={setPendingKnowledgeFiles}
            />

            <div className="relative py-1">
              <div className="absolute inset-x-0 top-1/2 h-px bg-[rgb(var(--border-subtle))]" aria-hidden="true" />
              <div className="relative flex justify-center">
                <span className="bg-[rgb(var(--surface-1))] px-3 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[rgb(var(--text-muted))]">
                  or paste inline
                </span>
              </div>
            </div>

            <div>
              <label htmlFor="knowledge-base" className="field-label">
                Knowledge base <span className="text-[rgb(var(--text-muted))] font-normal normal-case">(optional)</span>
              </label>
              <p className="field-hint -mt-1 mb-2">
                Quick freeform notes the AI pulls from — FAQs, product details, pricing rules. Files are usually easier for anything longer.
              </p>
              <textarea
                id="knowledge-base"
                className="field-input min-h-[120px] py-2.5 leading-relaxed"
                value={knowledgeBase} onChange={(e) => setKnowledgeBase(e.target.value)}
                placeholder={'Q: What services do you offer?\nA: We provide custom software development, cloud migration, and AI solutions.'}
                maxLength={10000}
              />
              <div className="flex items-center justify-end mt-1">
                <span className="text-[11px] text-[rgb(var(--text-muted))] tabular-nums">
                  {knowledgeBase.length.toLocaleString()}/10,000
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Behavior */}
      {step === 'behavior' && (
        <div className="space-y-6">
          <div className="wizard-card">
            <div>
              <div className="wizard-card__eyebrow">Response behavior</div>
              <p className="wizard-card__subtitle mt-1">
                How the AI interprets scope and how detailed its replies should be.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <span className="field-label">Strictness</span>
                <p className="field-hint -mt-1 mb-2">How strictly the AI stays within authorized topics.</p>
                <div className="segmented" role="group" aria-label="Strictness">
                  {(['STRICT', 'MODERATE', 'RELAXED'] as const).map((v) => (
                    <button
                      key={v} type="button" onClick={() => setStrictness(v)}
                      className="segmented__item" aria-pressed={strictness === v}
                    >
                      {v.charAt(0) + v.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <span className="field-label">Response length</span>
                <p className="field-hint -mt-1 mb-2">How verbose the AI responses should be.</p>
                <div className="segmented" role="group" aria-label="Response length">
                  {(['SHORT', 'MEDIUM', 'DETAILED'] as const).map((v) => (
                    <button
                      key={v} type="button" onClick={() => setResponseLength(v)}
                      className="segmented__item" aria-pressed={responseLength === v}
                    >
                      {v.charAt(0) + v.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <ModelChooser
              models={models}
              preferredModel={preferredModel}
              preferredModelLocked={preferredModelLocked}
              onSelect={setPreferredModel}
              onLockedClick={setUpgradeModel}
              onClearPreferred={() => setPreferredModel('')}
              loadError={modelsError}
              onRetry={refetchModels}
            />

            <Toggle
              label="Allow general tasks"
              description="Let the AI write emails, summarize, format content within scope."
              checked={allowGeneralTasks}
              onChange={setAllowGeneralTasks}
            />
          </div>

          <div className="wizard-card">
            <div>
              <div className="wizard-card__eyebrow">Advanced</div>
              <p className="wizard-card__subtitle mt-1">
                Optional fine-tuning for refusal messaging and custom behavior.
              </p>
            </div>

            <div>
              <label htmlFor="refusal-message" className="field-label">
                Refusal message <span className="text-[rgb(var(--text-muted))] font-normal normal-case">(optional)</span>
              </label>
              <p className="field-hint -mt-1 mb-2">Shown when the AI declines a request. Leave blank for the default.</p>
              <input
                id="refusal-message" className="field-input" value={refusalMessage}
                onChange={(e) => setRefusalMessage(e.target.value)}
                placeholder="This persona is not authorized to help with that request."
                maxLength={500}
              />
              {refusalMessage.length > 0 && (
                <div className="flex items-center justify-end mt-1">
                  <span className="text-[11px] text-[rgb(var(--text-muted))] tabular-nums">{refusalMessage.length}/500</span>
                </div>
              )}
            </div>

            {optionalRules && optionalRules.length > 0 && (
              <div>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="field-label">Enforcement rules</span>
                  <span className="text-[11px] text-[rgb(var(--text-muted))]">
                    {optionalRules.length - disabledRules.length} of {optionalRules.length} active
                  </span>
                </div>
                <p className="field-hint -mt-1 mb-3">
                  Toggle rules that apply to your persona. System rules are always enforced and don't appear here.
                </p>
                <div className="rounded-xl border border-[rgb(var(--border-subtle))] bg-[rgb(var(--surface-2))] divide-y divide-[rgb(var(--border-subtle))] overflow-hidden">
                  {groupRulesByCategory(optionalRules).map(([category, rules]) => (
                    <div key={category}>
                      <div className="px-4 py-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[rgb(var(--text-muted))] bg-[rgb(var(--surface-3)/0.6)]">
                        {category}
                      </div>
                      <ul className="divide-y divide-[rgb(var(--border-subtle))]">
                        {rules.map((rule) => {
                          const isEnabled = !disabledRules.includes(rule.id)
                          return (
                            <li key={rule.id} className="flex items-start gap-3 px-4 py-2.5">
                              <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-0.5"
                                aria-label={`${isEnabled ? 'Enabled' : 'Disabled'}: ${rule.text}`}>
                                <input
                                  type="checkbox" className="sr-only peer" checked={isEnabled}
                                  onChange={() => {
                                    if (isEnabled) setDisabledRules([...disabledRules, rule.id])
                                    else setDisabledRules(disabledRules.filter((id) => id !== rule.id))
                                  }}
                                />
                                <div className="w-8 h-[18px] bg-gray-300 dark:bg-gray-700 rounded-full peer peer-checked:bg-brand-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3.5 after:w-3.5 after:transition-all peer-checked:after:translate-x-[14px]" />
                              </label>
                              <span className="flex-1 text-[13px] leading-relaxed text-[rgb(var(--text-secondary))]">
                                {rule.text}
                              </span>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label htmlFor="ai-instructions" className="field-label">
                Custom instructions <span className="text-[rgb(var(--text-muted))] font-normal normal-case">(optional)</span>
              </label>
              <p className="field-hint -mt-1 mb-2">Additional rules or personality traits for the AI.</p>
              <textarea
                id="ai-instructions" className="field-input min-h-[100px] py-2.5 leading-relaxed"
                value={aiInstructions} onChange={(e) => setAiInstructions(e.target.value)}
                placeholder="e.g. Always greet visitors warmly. Mention our website in every response."
                maxLength={3000}
              />
              {aiInstructions.length > 0 && (
                <div className="flex items-center justify-end mt-1">
                  <span className="text-[11px] text-[rgb(var(--text-muted))] tabular-nums">
                    {aiInstructions.length.toLocaleString()}/3,000
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Review */}
      {step === 'review' && (
        <div className="space-y-5">
          <ReviewSection title="Identity" onEdit={() => setStep('identity')}>
            <ReviewItem label="Persona name" value={name} required />
            <ReviewItem label="Type" value={type === 'business' ? 'Business' : 'Individual'} />
            <ReviewItem label="Owner display" value={ownerDisplayName} />
          </ReviewSection>

          <ReviewSection title="Public presence" onEdit={() => setStep('identity')}>
            <ReviewItem
              label="Public URL"
              value={
                <span className="inline-flex items-center gap-1.5 font-mono text-[13px]">
                  {publicUrl}
                  <button
                    type="button"
                    onClick={() => { navigator.clipboard?.writeText(publicUrl); notify.success('URL copied.') }}
                    className="inline-flex items-center gap-1 text-[11px] text-[rgb(var(--text-muted))] hover:text-[rgb(var(--accent-strong))] transition-colors"
                  >
                    <Copy size={11} /> Copy
                  </button>
                </span>
              }
            />
            <ReviewItem label="Public bio" value={publicBio} full />
            <ReviewItem label="Tone" value={tone || 'Default'} />
          </ReviewSection>

          <ReviewSection title="AI notes" onEdit={() => setStep('identity')}>
            <ReviewItem label="Internal notes" value={description} full />
          </ReviewSection>

          <ReviewSection title="Scope" onEdit={() => setStep('scope')}>
            <div>
              <div className="review-item__label">Can help with</div>
              <div className="tag-list mt-2">
                {canHelpWith.length === 0
                  ? <span className="review-item__value review-item__value--empty">No topics added</span>
                  : canHelpWith.map((t) => <span key={t} className="tag-chip tag-chip--positive">{t}</span>)}
              </div>
            </div>
            <div>
              <div className="review-item__label">Cannot help with</div>
              <div className="tag-list mt-2">
                {cannotHelpWith.length === 0
                  ? <span className="review-item__value review-item__value--empty">No restrictions</span>
                  : cannotHelpWith.map((t) => <span key={t} className="tag-chip tag-chip--danger">{t}</span>)}
              </div>
            </div>
            <ReviewItem label="Boundary rules" value={boundaries} full />
          </ReviewSection>

          <ReviewSection title="Knowledge" onEdit={() => setStep('scope')}>
            <div className="sm:col-span-2">
              <div className="review-item__label flex items-center justify-between">
                <span>Knowledge sources</span>
                {knowledgeSources.length > 0 && (
                  <span className="text-[11px] font-normal text-[rgb(var(--text-muted))] normal-case tracking-normal">
                    {humanBytes(knowledgeSources.reduce((t, s) => t + s.sizeBytes, 0))} total
                  </span>
                )}
              </div>
              {knowledgeSources.length === 0 ? (
                <div className="review-item__value review-item__value--empty">None uploaded</div>
              ) : (
                <ul className="mt-2 space-y-1.5">
                  {knowledgeSources.map((s) => (
                    <li
                      key={s.id}
                      className="flex items-center gap-2 p-2 rounded-md bg-[rgb(var(--surface-2))] border border-[rgb(var(--border-subtle))]"
                    >
                      <FileText size={14} className="text-[rgb(var(--text-muted))] flex-shrink-0" />
                      <span className="text-[13px] text-[rgb(var(--text-primary))] truncate flex-1 min-w-0">{s.filename}</span>
                      <span className="text-[11px] text-[rgb(var(--text-muted))] tabular-nums flex-shrink-0">
                        {humanBytes(s.sizeBytes)}
                      </span>
                      {s.status === 'READY' ? (
                        <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 h-5 rounded bg-emerald-500/15 text-emerald-400 flex-shrink-0">
                          <Check size={10} /> Ready
                        </span>
                      ) : s.status === 'PROCESSING' ? (
                        <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 h-5 rounded bg-amber-500/15 text-amber-400 flex-shrink-0">
                          <RefreshCcw size={10} className="animate-spin" /> Processing
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 h-5 rounded bg-red-500/15 text-red-400 flex-shrink-0">
                          <AlertTriangle size={10} /> Failed
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <ReviewItem
              label="Knowledge base"
              value={knowledgeBase ? `${knowledgeBase.length.toLocaleString()} characters` : 'Empty'}
            />
          </ReviewSection>

          <ReviewSection title="Behavior" onEdit={() => setStep('behavior')}>
            <ReviewItem label="Strictness" value={strictness.charAt(0) + strictness.slice(1).toLowerCase()} />
            <ReviewItem label="Response length" value={responseLength.charAt(0) + responseLength.slice(1).toLowerCase()} />
            <ReviewItem label="AI model" value={models?.find((m) => m.id === preferredModel)?.label || 'System default'} />
            <ReviewItem label="General tasks" value={allowGeneralTasks ? 'Allowed' : 'Off'} />
            <ReviewItem label="Custom refusal" value={refusalMessage || 'Default'} />
          </ReviewSection>

          {/* Consent required — new persona, or re-consent after scope/boundary changes */}
          {!existing?.isActive && (
            <div className="wizard-card border border-[rgb(var(--accent)/0.4)] bg-[rgb(var(--accent)/0.06)]">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand-500/15 text-brand-500 dark:text-brand-300 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">
                    {existing?.id && !existing?.consentedAt ? 'Consent & activate'
                      : existing?.id ? 'Re-consent required'
                      : 'Consent & activate'}
                  </h3>
                  {existing?.id && existing?.consentedAt && (
                    <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 text-sm text-amber-600 dark:text-amber-300 border border-amber-500/20">
                      <Shield size={14} className="flex-shrink-0 mt-0.5" />
                      Your scope or boundary rules changed. Re-consent is required before your persona goes live again.
                    </div>
                  )}
                  <blockquote className="mt-4 p-4 rounded-lg border border-[rgb(var(--border-subtle))] bg-[rgb(var(--surface-2))] text-sm leading-relaxed italic text-[rgb(var(--text-secondary))]">
                    "I authorize this AI persona to represent me within the boundaries I have defined.
                    I understand that this persona will only respond within its defined scope,
                    and I can pause or delete it at any time."
                  </blockquote>
                  <button
                    type="button" onClick={handleConsent} disabled={saving}
                    className="btn-primary mt-4 w-full sm:w-auto justify-center"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                    Agree & activate
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sticky action bar */}
      <div className="wizard-actions">
        <div>
          {step !== 'identity' ? (
            <button type="button" onClick={goBack} className="btn-secondary">
              <ArrowLeft size={14} /> Back
            </button>
          ) : (
            <button type="button" onClick={() => navigate('/dashboard')} className="btn-secondary">
              Cancel
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {step === 'identity' && (
            <button
              type="button" onClick={goNext} className="btn-primary"
              disabled={!canProceedIdentity}
            >
              Continue <ArrowRight size={14} />
            </button>
          )}
          {step === 'scope' && (
            <button
              type="button" onClick={goNext} className="btn-primary"
              disabled={!canProceedScope}
            >
              Continue <ArrowRight size={14} />
            </button>
          )}
          {step === 'behavior' && (
            <button
              type="button" onClick={goNext} className="btn-primary"
              disabled={saving || !canProceedBehavior}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              Save & review <ArrowRight size={14} />
            </button>
          )}
          {step === 'review' && existing?.isActive && (
            <button type="button" onClick={() => navigate('/dashboard')} className="btn-primary">
              <Check size={14} /> Done
            </button>
          )}
        </div>
      </div>

      {cropSrc && (
        <AvatarCropper
          imageSrc={cropSrc}
          onCancel={() => setCropSrc(null)}
          onSave={async (blob) => {
            const reader = new FileReader()
            reader.onload = () => {
              if (typeof reader.result === 'string') setPersonaPhoto(reader.result)
              setCropSrc(null)
            }
            reader.readAsDataURL(blob)
          }}
        />
      )}

      {upgradeModel && (
        <ModelUpgradeModal
          model={upgradeModel}
          onClose={() => setUpgradeModel(null)}
        />
      )}
    </div>
  )
}

/* ──────────────────────────────────────────────────────────
 * Internal helper components
 * ────────────────────────────────────────────────────────── */

function StatusChip({ tone, children }: { tone: 'success' | 'warn' | 'neutral'; children: React.ReactNode }) {
  const classes =
    tone === 'success' ? 'bg-emerald-500/15 text-emerald-500 dark:text-emerald-300 border-emerald-500/30' :
    tone === 'warn'    ? 'bg-amber-500/15 text-amber-600 dark:text-amber-300 border-amber-500/30' :
                         'bg-[rgb(var(--surface-3)/0.7)] text-[rgb(var(--text-secondary))] border-[rgb(var(--border-subtle))]'
  return (
    <span className={`inline-flex items-center gap-1.5 h-6 px-2 rounded-full text-[11px] font-semibold uppercase tracking-wide border ${classes}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden="true" />
      {children}
    </span>
  )
}

function TypeChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-semibold tracking-wide text-[rgb(var(--text-secondary))] bg-[rgb(var(--surface-3)/0.6)] border border-[rgb(var(--border-subtle))]">
      {children}
    </span>
  )
}

function PersonaPhotoUploader({
  photoUrl, onChoose, onClear,
}: {
  photoUrl: string
  onChoose: (src: string) => void
  onClear: () => void
}) {
  return (
    <div className="flex-shrink-0">
      <div className="relative group w-24 h-24">
        {photoUrl ? (
          <img src={photoUrl} alt="Persona" className="w-24 h-24 rounded-2xl object-cover border border-[rgb(var(--border-subtle))]" />
        ) : (
          <div className="w-24 h-24 rounded-2xl bg-[rgb(var(--surface-2))] border-2 border-dashed border-[rgb(var(--border-strong))] flex flex-col items-center justify-center gap-1">
            <Camera size={20} className="text-[rgb(var(--text-muted))]" />
            <span className="text-[10px] text-[rgb(var(--text-muted))]">Photo</span>
          </div>
        )}
        <div className="absolute inset-0 rounded-2xl bg-black/55 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center justify-center gap-1">
          <label className="p-2 rounded-full bg-white/20 hover:bg-white/30 cursor-pointer transition-colors" title="Change photo">
            <Camera size={16} className="text-white" />
            <input
              type="file" accept="image/*" className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (!file) return
                if (!file.type.startsWith('image/')) { notify.error('Please select an image file (PNG, JPG, or GIF).'); return }
                if (file.size > 5 * 1024 * 1024) { notify.error('The file size must not exceed 5 MB.'); return }
                const reader = new FileReader()
                reader.onload = () => { if (typeof reader.result === 'string') onChoose(reader.result) }
                reader.readAsDataURL(file)
                e.target.value = ''
              }}
            />
          </label>
          {photoUrl && (
            <button
              type="button" onClick={onClear}
              className="p-2 rounded-full bg-white/20 hover:bg-red-500/60 transition-colors"
              title="Remove"
            >
              <Trash2 size={16} className="text-white" />
            </button>
          )}
        </div>
      </div>
      <p className="text-[11px] text-[rgb(var(--text-muted))] mt-2 text-center">PNG, JPG up to 5MB</p>
    </div>
  )
}

function HandleField({
  isEditing, changingHandle, handle, onHandleChange, handleValid, handleAvailable, handleError,
  newHandle, onNewHandleChange, newHandleAvailable, newHandleError,
  onStartChange, onCancelChange, onConfirmChange, saving, publicUrl,
}: {
  isEditing: boolean
  changingHandle: boolean
  handle: string
  onHandleChange: (v: string) => void
  handleValid: boolean | null
  handleAvailable: boolean | null
  handleError: string | null
  newHandle: string
  onNewHandleChange: (v: string) => void
  newHandleAvailable: boolean | null
  newHandleError: string | null
  onStartChange: () => void
  onCancelChange: () => void
  onConfirmChange: () => void
  saving: boolean
  publicUrl: string
}) {
  if (isEditing && !changingHandle) {
    return (
      <div>
        <span className="field-label">Public handle <span className="text-red-400">*</span></span>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center h-10 px-3 rounded-lg bg-[rgb(var(--surface-2))] border border-[rgb(var(--border-subtle))] text-sm font-mono text-[rgb(var(--text-secondary))]">
            <span className="opacity-60">idfy.studio/p/</span>
            <span className="text-[rgb(var(--text-primary))] font-medium">{handle}</span>
          </span>
          <button type="button" onClick={onStartChange} className="btn-secondary text-sm">
            <Pencil size={13} /> Change
          </button>
          <button
            type="button"
            onClick={() => { navigator.clipboard?.writeText(publicUrl); notify.success('URL copied.') }}
            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg text-sm text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text-primary))] transition-colors"
          >
            <Copy size={13} /> Copy URL
          </button>
        </div>
      </div>
    )
  }

  if (isEditing && changingHandle) {
    return (
      <div className="space-y-3">
        <span className="field-label">Public handle</span>
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 text-sm text-amber-600 dark:text-amber-300 border border-amber-500/25">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Changing your handle is a breaking change</p>
            <ul className="mt-1 text-xs space-y-0.5 list-disc list-inside">
              <li>All existing invite links will stop working</li>
              <li>All active invite tokens will be revoked</li>
              <li>Your persona will be deactivated until you re-consent</li>
            </ul>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[rgb(var(--text-muted))] whitespace-nowrap">idfy.studio/p/</span>
          <input
            className="field-input flex-1 font-mono" value={newHandle}
            onChange={(e) => onNewHandleChange(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="new-handle" autoFocus
            aria-invalid={Boolean(newHandleError) || undefined}
          />
        </div>
        {newHandleAvailable === true && !newHandleError && (
          <p className="text-xs text-emerald-500 flex items-center gap-1"><Check size={12} /> Available</p>
        )}
        {newHandleError && <p className="field-error">{newHandleError}</p>}
        <div className="flex gap-2">
          <button type="button" onClick={onCancelChange} className="btn-secondary text-sm">Cancel</button>
          <button
            type="button" onClick={onConfirmChange}
            disabled={saving || !newHandleAvailable || Boolean(newHandleError)}
            className="btn-danger text-sm"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            Confirm change
          </button>
        </div>
      </div>
    )
  }

  // Create flow
  return (
    <div>
      <label htmlFor="handle" className="field-label">Public handle <span className="text-red-400">*</span></label>
      <p className="field-hint -mt-1 mb-2">Your persona's public URL. Lowercase letters, numbers, and hyphens only.</p>
      <div className="flex items-stretch">
        <span className="inline-flex items-center px-3 rounded-l-lg bg-[rgb(var(--surface-3)/0.5)] border border-r-0 border-[rgb(var(--border-subtle))] text-sm text-[rgb(var(--text-muted))] font-mono">
          idfy.studio/p/
        </span>
        <input
          id="handle"
          className="field-input rounded-l-none flex-1 font-mono"
          value={handle}
          onChange={(e) => onHandleChange(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
          placeholder="your-handle" required
          aria-invalid={handleAvailable === false || handleValid === false || undefined}
        />
      </div>
      {handleAvailable === true && handleValid !== false && (
        <p className="text-xs text-emerald-500 mt-1.5 flex items-center gap-1"><Check size={12} /> Available</p>
      )}
      {handleAvailable === false && !handleError && (
        <p className="field-error">This handle is not available</p>
      )}
      {handleError && <p className="field-error">{handleError}</p>}
    </div>
  )
}

function TagInput({
  id, label, required, helper, value, onChange, current, onCurrentChange,
  placeholder, examples, tone, onAdd,
}: {
  id: string
  label: string
  required?: boolean
  helper: string
  value: string[]
  onChange: (next: string[]) => void
  current: string
  onCurrentChange: (v: string) => void
  placeholder?: string
  examples?: string[]
  tone: 'positive' | 'danger'
  onAdd: () => void
}) {
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}{required ? <span className="text-red-400"> *</span> : null}
      </label>
      <p className="field-hint -mt-1 mb-2">{helper}</p>
      {value.length > 0 && (
        <div className="tag-list mb-2">
          {value.map((item, i) => (
            <span key={`${item}-${i}`} className={`tag-chip ${tone === 'positive' ? 'tag-chip--positive' : 'tag-chip--danger'}`}>
              {item}
              <button
                type="button"
                onClick={() => onChange(value.filter((_, j) => j !== i))}
                className="tag-chip__remove"
                aria-label={`Remove ${item}`}
              >
                <X size={11} strokeWidth={2.5} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          id={id}
          className="field-input flex-1"
          value={current}
          onChange={(e) => onCurrentChange(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); onAdd() }
            if (e.key === ',') { e.preventDefault(); onAdd() }
          }}
        />
        <button
          type="button" onClick={onAdd}
          className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-[rgb(var(--border-subtle))] text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--text-primary))] hover:bg-[rgb(var(--surface-3)/0.6)] transition-colors"
          aria-label={`Add ${label.toLowerCase()} tag`}
          disabled={!current.trim()}
        >
          <Plus size={16} />
        </button>
      </div>
      {value.length === 0 && examples && examples.length > 0 && (
        <p className="field-hint mt-2">
          Try: {examples.map((ex, i) => (
            <button
              key={ex} type="button"
              onClick={() => { onCurrentChange(ex); setTimeout(onAdd, 0) }}
              className="underline decoration-dotted decoration-[rgb(var(--text-muted))] underline-offset-2 hover:text-[rgb(var(--text-primary))] transition-colors"
            >{ex}{i < examples.length - 1 ? ', ' : ''}</button>
          ))}
        </p>
      )}
    </div>
  )
}

function ModelOption({
  selected, locked, onClick, title, description, meta, tier, isRadio,
}: {
  selected: boolean
  locked?: boolean
  onClick: () => void
  title: string
  description?: string
  meta?: string
  /** STANDARD | ADVANCED | PREMIUM from the MMS-resolved model catalog. */
  tier?: string
  /** When true, renders with role="radio" semantics for arrow-key grid nav.
   *  Locked cards always drop back to a plain button — clicking them opens
   *  the upgrade modal, so "radio" would mis-announce the action to AT. */
  isRadio?: boolean
}) {
  const upperTier = (tier || '').toUpperCase()
  const showTierChip = !locked && upperTier && upperTier !== 'STANDARD'
  const tierChipClass = upperTier === 'PREMIUM'
    ? 'bg-amber-500/15 text-amber-400'
    : 'bg-brand-500/15 text-brand-400'

  // Only behave as a radio when the card is actually selectable.
  const behaveAsRadio = Boolean(isRadio && !locked)

  return (
    <button
      type="button"
      onClick={onClick}
      data-model-radio={behaveAsRadio ? 'true' : undefined}
      role={behaveAsRadio ? 'radio' : undefined}
      aria-checked={behaveAsRadio ? selected : undefined}
      aria-label={locked ? `${title} — upgrade required` : undefined}
      // Radio-group convention: only the selected radio is in the tab sequence;
      // arrow keys move between the others. Locked cards stay tabbable so they
      // can still be reached and activated with Enter/Space.
      tabIndex={behaveAsRadio ? (selected ? 0 : -1) : 0}
      className={`group text-left p-3 rounded-lg border transition-all
        focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent)/0.5)] ${
        locked
          ? 'border-[rgb(var(--border-subtle))] bg-[rgb(var(--surface-2))] cursor-pointer hover:border-amber-500/40 hover:bg-amber-500/5'
          : selected
            ? 'border-brand-500 bg-brand-500/10 ring-1 ring-brand-500/60'
            : 'border-[rgb(var(--border-subtle))] bg-[rgb(var(--surface-1))] hover:border-[rgb(var(--border-strong))] hover:bg-[rgb(var(--surface-2))]'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`text-sm font-medium ${locked ? 'text-[rgb(var(--text-secondary))]' : 'text-gray-900 dark:text-gray-100'}`}>
          {title}
        </span>
        {locked ? (
          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 h-5 rounded bg-amber-500/15 text-amber-400 font-semibold uppercase tracking-wide">
            Upgrade
          </span>
        ) : selected ? (
          <Check size={14} className="text-brand-500" />
        ) : null}
      </div>
      {description && <p className="text-xs text-[rgb(var(--text-muted))] mt-0.5">{description}</p>}
      {(meta || showTierChip) && (
        <div className="flex items-center gap-2 mt-1.5">
          {meta && <span className="text-[10px] text-[rgb(var(--text-muted))] uppercase tracking-wider">{meta}</span>}
          {showTierChip && (
            <span className={`text-[10px] px-1.5 h-4 inline-flex items-center rounded font-semibold uppercase tracking-wide ${tierChipClass}`}>
              {upperTier}
            </span>
          )}
        </div>
      )}
    </button>
  )
}

function Toggle({
  label, description, checked, onChange,
}: {
  label: string
  description?: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-4 p-4 rounded-lg bg-[rgb(var(--surface-2))] border border-[rgb(var(--border-subtle))]">
      <div className="min-w-0">
        <span className="field-label mb-0">{label}</span>
        {description && <p className="text-xs text-[rgb(var(--text-muted))] mt-0.5">{description}</p>}
      </div>
      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
        <input type="checkbox" className="sr-only peer" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <div className="w-10 h-5 bg-gray-300 dark:bg-gray-700 rounded-full peer peer-checked:bg-brand-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5" />
      </label>
    </div>
  )
}

function ReviewSection({
  title, onEdit, children,
}: {
  title: string
  onEdit: () => void
  children: React.ReactNode
}) {
  return (
    <div className="review-section">
      <div className="review-section__header">
        <h3 className="review-section__title">{title}</h3>
        <button type="button" onClick={onEdit} className="review-section__edit">
          <Pencil size={11} /> Edit
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        {children}
      </div>
    </div>
  )
}

function ReviewItem({
  label, value, full, required,
}: {
  label: string
  value: React.ReactNode
  full?: boolean
  required?: boolean
}) {
  const isEmpty = value === '' || value === undefined || value === null
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <div className="review-item__label">{label}</div>
      {isEmpty ? (
        <div className="review-item__value review-item__value--empty">
          {required ? 'Required — missing' : 'Not set'}
        </div>
      ) : typeof value === 'string' ? (
        <div className="review-item__value whitespace-pre-line">{value}</div>
      ) : (
        <div className="review-item__value">{value}</div>
      )}
    </div>
  )
}

function humanBytes(bytes: number): string {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  let n = bytes
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`
}

/* ──────────────────────────────────────────────────────────
 * AI model chooser — Phase 1 behavior-step refinement.
 *
 * Splits "System default" into its own "Recommended" row, groups the rest
 * under an "Override" grid with radio-group semantics and arrow-key
 * navigation, promotes locked cards from inert to clickable (opens the
 * upgrade modal), and handles catalog-fetch failures without blocking the
 * persona edit.
 * ────────────────────────────────────────────────────────── */

function ModelChooser({
  models, preferredModel, preferredModelLocked,
  onSelect, onLockedClick, onClearPreferred,
  loadError, onRetry,
}: {
  models: AvailableModel[] | undefined
  preferredModel: string
  preferredModelLocked: boolean
  onSelect: (id: string) => void
  onLockedClick: (m: AvailableModel) => void
  onClearPreferred: () => void
  loadError: boolean
  onRetry: () => void
}) {
  const systemDefaultSelected = !preferredModel
  const overrideModels = models || []

  // Arrow-key radio-group navigation across the override grid.
  const onOverrideKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const key = e.key
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return
    const buttons = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>('[data-model-radio]')
    )
    if (buttons.length === 0) return
    const currentIdx = buttons.findIndex((b) => b === document.activeElement)
    let next = currentIdx
    if (key === 'ArrowDown' || key === 'ArrowRight') next = (currentIdx + 1 + buttons.length) % buttons.length
    else if (key === 'ArrowUp' || key === 'ArrowLeft') next = (currentIdx - 1 + buttons.length) % buttons.length
    else if (key === 'Home') next = 0
    else if (key === 'End') next = buttons.length - 1
    if (next !== currentIdx) { e.preventDefault(); buttons[next]?.focus() }
  }

  return (
    <div className="space-y-3">
      <div>
        <span className="field-label">AI model</span>
        <p className="field-hint -mt-1 mb-3">
          Your persona runs on <span className="text-[rgb(var(--text-secondary))]">System default</span> unless you override.
          Larger models produce better responses at higher cost.
        </p>
      </div>

      {preferredModelLocked && (
        <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 flex flex-wrap items-start gap-3">
          <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 text-sm text-amber-300">
            Your current plan no longer includes this model. Pick another or switch back to System default to continue.
          </div>
          <button
            type="button"
            onClick={onClearPreferred}
            className="btn-secondary text-xs"
          >
            Use System default
          </button>
        </div>
      )}

      {loadError && (
        <div className="rounded-lg border border-[rgb(var(--border-subtle))] bg-[rgb(var(--surface-2))] p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <AlertTriangle size={14} className="text-[rgb(var(--text-muted))] flex-shrink-0 mt-0.5" />
            <span className="text-xs text-[rgb(var(--text-muted))]">
              Model catalog is temporarily unavailable — your persona will use System default.
            </span>
          </div>
          <button
            type="button"
            onClick={onRetry}
            className="text-xs text-brand-400 hover:text-brand-300 inline-flex items-center gap-1"
          >
            Retry
          </button>
        </div>
      )}

      {/* Recommended — System default */}
      <div className="space-y-2">
        <div className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[rgb(var(--text-muted))]">
          Recommended
        </div>
        <ModelOption
          selected={systemDefaultSelected}
          onClick={onClearPreferred}
          title="System default"
          description="Auto-selected per conversation complexity. Best for most personas."
          isRadio
        />
      </div>

      {/* Override grid */}
      {overrideModels.length > 0 && (
        <div className="space-y-2 pt-1">
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[rgb(var(--text-muted))]">
            Override with a specific model
          </div>
          <div
            role="radiogroup"
            aria-label="Override AI model"
            onKeyDown={onOverrideKey}
            className="grid grid-cols-1 sm:grid-cols-2 gap-2"
          >
            {overrideModels.map((m) => (
              <ModelOption
                key={m.id}
                selected={preferredModel === m.id}
                locked={Boolean(m.locked)}
                onClick={() => m.locked ? onLockedClick(m) : onSelect(m.id)}
                title={m.label}
                description={m.description}
                meta={m.provider}
                tier={m.tier}
                isRadio
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function ModelUpgradeModal({
  model, onClose,
}: {
  model: AvailableModel
  onClose: () => void
}) {
  const tier = (model.tier || '').toUpperCase()
  const isPremium = tier === 'PREMIUM'
  const headline = isPremium
    ? `${model.label} is a frontier model`
    : `${model.label} needs a plan upgrade`
  const rationale = isPremium
    ? 'Available on our top tiers for teams that need the highest reasoning quality.'
    : 'Stronger reasoning than the defaults — available on paid tiers.'

  const panelRef = useRef<HTMLDivElement>(null)

  // Escape closes + keep focus inside the dialog while it's open. Matches
  // the AccountModal pattern already in use elsewhere.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return }
      if (e.key !== 'Tab') return
      const items = getTabbables(panelRef.current)
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault() }
      else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault() }
    }
    window.addEventListener('keydown', onKey)
    // Move initial focus into the dialog's first interactive element.
    const t = window.setTimeout(() => {
      const items = getTabbables(panelRef.current)
      items[0]?.focus()
    }, 0)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.clearTimeout(t)
      // Return focus to the element that opened the dialog.
      previouslyFocused?.focus?.()
    }
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="model-upgrade-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div className="modal-backdrop" onClick={onClose} aria-hidden="true" />
      <div ref={panelRef} className="modal-panel relative w-full max-w-md rounded-2xl">
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-500/15 text-brand-400 flex items-center justify-center flex-shrink-0">
              <Settings size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 id="model-upgrade-title" className="text-[15px] font-semibold text-gray-900 dark:text-gray-100">
                {headline}
              </h3>
              <p className="text-sm text-[rgb(var(--text-muted))] mt-1">
                {rationale}
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-[rgb(var(--border-subtle))] bg-[rgb(var(--surface-2))] p-4 space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-[rgb(var(--text-primary))]">{model.label}</span>
              {tier && tier !== 'STANDARD' && (
                <span className={`inline-flex items-center h-5 px-2 rounded text-[10px] font-semibold uppercase tracking-wide ${
                  tier === 'PREMIUM'
                    ? 'bg-amber-500/15 text-amber-400'
                    : 'bg-brand-500/15 text-brand-400'
                }`}>
                  {tier}
                </span>
              )}
            </div>
            {model.description && (
              <p className="text-xs text-[rgb(var(--text-muted))]">{model.description}</p>
            )}
            {model.provider && (
              <p className="text-[10.5px] text-[rgb(var(--text-muted))] uppercase tracking-wider mt-1">{model.provider}</p>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary text-sm">
              Not now
            </button>
            <Link to="/plan" onClick={onClose} className="btn-primary text-sm">
              See plans
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}


/**
 * Group enforcement rules by their category, preserving the order categories
 * first appear in the source list. Stable ordering avoids categories dancing
 * around between renders as the server shuffles items.
 */
function groupRulesByCategory<T extends { category: string }>(rules: T[]): Array<[string, T[]]> {
  const order: string[] = []
  const map = new Map<string, T[]>()
  for (const r of rules) {
    const cat = (r.category || 'Other').trim() || 'Other'
    if (!map.has(cat)) { map.set(cat, []); order.push(cat) }
    map.get(cat)!.push(r)
  }
  return order.map((c) => [c, map.get(c)!])
}

/** Tab-stop collector used by the modal focus trap. */
function getTabbables(container: HTMLElement | null): HTMLElement[] {
  if (!container) return []
  const sel = [
    'a[href]', 'area[href]', 'input:not([disabled])', 'select:not([disabled])',
    'textarea:not([disabled])', 'button:not([disabled])', '[tabindex]:not([tabindex="-1"])',
  ].join(',')
  return Array.from(container.querySelectorAll<HTMLElement>(sel))
    .filter((el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null)
}
